import { useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { log } from 'log';
import { pick } from 'ramda';
import { useGetBingoJackpots } from 'server';

import * as helpers from '@core/js/hooks/useGetJackpots/helpers';
import * as server from '@core/js/hooks/useGetJackpots/server';
import * as actions from '@core/js/hooks/useGetJackpots/store/actions';
import { useAppSelector, useUserSelector } from '@core/js/store/hooks';
import { getXML } from '@core/js/utils/fetch.util';

import { useDynamicTags } from '@interlayer/js/custom/hooks/useDynamicTags';
import { DYNAMIC_TAG_NAMES } from '@interlayer/js/custom/utils/constants';
import { parseJackpotsXMLBP } from '@interlayer/js/hooks/useGetJackpots/sun/helpers';

import { fetchExternalJackpot } from './sun/store/actions';

// Sun: add useEffect (line 89 - 101line)
export const useGetJackpots = () => {
    const dispatch = useDispatch();
    const getBingoJackpots = useGetBingoJackpots();
    const { country, currencyCode } = useUserSelector(pick(['country', 'currencyCode']));
    const {
        jackpotsCasinoUrl, jackpotsCasino, useServicesCasinoJackpots,
        locationJson, casinoName, useServicesVfJackpots, defaultBingoRegion
    } = useAppSelector(pick([
        'jackpotsCasinoUrl', 'jackpotsCasino', 'useServicesCasinoJackpots',
        'locationJson', 'casinoName', 'useServicesVfJackpots', 'defaultBingoRegion'
    ]));

    useEffect(() => {
        dispatch(actions.clearJackpots());
    }, [dispatch, currencyCode]);

    useEffect(() => {
        if (jackpotsCasinoUrl && jackpotsCasino && currencyCode && useServicesCasinoJackpots) {
            (async () => {
                try {
                    const { jackpots, casinoJackpot } = await server.getJackpots({
                        jackpotsCasinoUrl, jackpotsCasino, currencyCode
                    });

                    dispatch(actions.setJackpots(jackpots));
                    dispatch(actions.setTotalCasinoJackpot(casinoJackpot));
                } catch (error) {
                    log('error', '[Jackpots] unable to get casino jackpots', error);
                }
            })();
        }
    }, [useServicesCasinoJackpots, jackpotsCasinoUrl, jackpotsCasino, currencyCode, dispatch]);

    useEffect(() => {
        if (!currencyCode || !casinoName || !useServicesVfJackpots) {
            return;
        }

        const items = locationJson ? locationJson.items : [];
        const regionItem = items?.find(location => location.currency === currencyCode && location.countryCode === country);
        const region = regionItem?.region || defaultBingoRegion;

        if (!region) {
            return;
        }

        (async () => {
            try {
                const response = await getBingoJackpots({ region });
                const bingoJackpots = helpers.parseVFJackpots(response.jackpots);

                dispatch(actions.setJackpots(bingoJackpots));
            } catch (error) {
                log('error', '[Jackpots] unable to get bingo jackpots', error);
            }
        })();
    }, [
        country,
        dispatch,
        casinoName,
        currencyCode,
        locationJson,
        getBingoJackpots,
        defaultBingoRegion,
        useServicesVfJackpots
    ]);

    const [dTagJackpotCasinoUrl] = useDynamicTags([DYNAMIC_TAG_NAMES.BLUEPRINT]);

    useEffect(() => {
        if (dTagJackpotCasinoUrl) {
            getXML(dTagJackpotCasinoUrl)
                .then((xmlDoc) => {
                    const jackpotsData = parseJackpotsXMLBP(xmlDoc);

                    dispatch(fetchExternalJackpot({ externalJackpots: jackpotsData }));
                })
                .catch((error) => {
                    throw error;
                });
        }
    }, [dTagJackpotCasinoUrl, dispatch]);
};
